import { useEffect, useRef } from 'react'
import {
  layoutSyncBody,
  readLayoutSync,
  shouldBroadcastLayout,
  type LayoutMode,
} from '../services/layout-sync'
import { vclog } from './vc-log'

/**
 * Keep the room on the moderator's layout while "Sync layout to peers" is on.
 *
 * The rules live in `layout-sync`; this is the wiring. It sends when the
 * moderator's layout changes and applies what arrives, on the generic
 * `broadcast` event — see that module for why a bespoke event would take the
 * channel process down.
 *
 * ## Following, and breaking away
 *
 * A participant who picks their own layout stops following until the moderator
 * pushes again. Without that the feature is a lock rather than a default: the
 * moderator's last push would be re-applied on every re-render, and someone who
 * needed the speaker large would be dragged back to the grid with no way to
 * resist short of asking the host to turn the switch off.
 *
 * Breaking away is deliberately silent and deliberately local. Nobody is told,
 * because a room full of "X changed their layout" is noise, and the moderator
 * keeps the room on their layout for everyone who has not moved.
 */
export type UseLayoutSyncParams = {
  /** The layout this client is in. */
  layoutMode: LayoutMode
  /** Apply a layout that arrived from the moderator. */
  setLayoutMode: (mode: LayoutMode) => void
  /** Is the switch on for this client? Only meaningful for a moderator. */
  enabled: boolean
  isModerator: boolean
  localPeerId: string | null | undefined
  /** Channel is up; before this, sending is pointless and receiving is empty. */
  ready: boolean
  subscribe: (event: string, handler: (payload: unknown) => void) => () => void
  sendEvent: (event: string, payload: Record<string, unknown>) => void
}

export function useLayoutSync({
  layoutMode,
  setLayoutMode,
  enabled,
  isModerator,
  localPeerId,
  ready,
  subscribe,
  sendEvent,
}: UseLayoutSyncParams): void {
  /** The last layout this client pushed, so an unchanged one is not re-sent. */
  const lastSentRef = useRef<LayoutMode | null>(null)

  /**
   * Set when a layout is applied FROM a broadcast, and compared on the next
   * change. It is what separates "the moderator moved me" from "I moved
   * myself": applying a pushed layout is itself a layout change, and without
   * this the receiver would read its own compliance as a break-away and stop
   * following after the very first push.
   */
  const appliedRef = useRef<LayoutMode | null>(null)
  const followingRef = useRef(true)

  const setLayoutRef = useRef(setLayoutMode)
  setLayoutRef.current = setLayoutMode

  // ── Receive ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return
    const off = subscribe('broadcast', (payload) => {
      const mode = readLayoutSync(payload, localPeerId)
      if (!mode) return
      vclog('stage', 'following the room layout', { mode })
      appliedRef.current = mode
      followingRef.current = true
      setLayoutRef.current(mode)
    })
    return off
  }, [ready, subscribe, localPeerId])

  // ── Break away ────────────────────────────────────────────────────────────
  // A change this client did not receive is one it made, so it stops following.
  useEffect(() => {
    if (appliedRef.current !== null && appliedRef.current !== layoutMode) {
      followingRef.current = false
      appliedRef.current = null
    }
  }, [layoutMode])

  // ── Send ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return
    if (!shouldBroadcastLayout({ isModerator, enabled, mode: layoutMode, lastSent: lastSentRef.current })) {
      // Turning the switch off clears the record, so turning it back on pushes
      // the current layout again rather than staying silent because that layout
      // happens to be the one last sent.
      if (!enabled) lastSentRef.current = null
      return
    }
    lastSentRef.current = layoutMode
    sendEvent('broadcast', { body: layoutSyncBody(layoutMode, localPeerId) })
    vclog('stage', 'pushed layout to the room', { mode: layoutMode })
  }, [ready, isModerator, enabled, layoutMode, localPeerId, sendEvent])
}
